import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { AppService } from '../../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  gridPayloadFromActiveScreen,
} from '../../../../shared/workspace-element-context';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';
import { JsonObjectFactoryService } from '../../../../shared/_services/json-object-factory.service';
import { K2K3SyncConfigService } from './k2-k3-sync-config.service';
import * as _ from 'lodash-es';

// Note: cronstrue library would need to be installed if not available
// For now, we'll handle cron expression display with a placeholder
declare var cronstrue: any;

@Component({
    selector: 'gtw-ftc-sync-config',
    templateUrl: './k2-k3-sync-config.component.html',
    styleUrls: ['./k2-k3-sync-config.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class K2K3SyncConfigComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  // Standard component properties
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  loadingData = true;

  // Global service data
  globalService: any;
  USER_SETTINGS: any;
  GENERAL_CONFIG: any;
  SERVER_CONFIG: any;

  // Component data
  private headerData: any = {};
  gridData: any;
  filterParams: any;

  // Form data properties
  sync_id: number = 0;
  configObj: any = {};
  selectedGridRow: any[] = [];
  is_locked: any = null;
  logged_in_user: string = '';
  scenario: string = '';
  jcd_key: string = '';
  filterTaxYear: string = '';

  dateOptions: any = {};

  // Flag to track if data was saved (for grid refresh)
  savedOnce: boolean = false;
  args: any = {};

  // Initialization flag to prevent duplicate calls
  private isInitialized = false;
  private isLoading = false;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private jsonObjectFactory: JsonObjectFactoryService,
    private ftcSyncConfigService: K2K3SyncConfigService
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    this.isLoading = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const fp = normalizeFilterParams(activeScreen);
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.headerData = { ...header, workspaceFactory: this.gtwWorkspace.workspaceFactory };
    const gp = this.gtwGlobal.hydrateGlobalParamsFromContext(activeScreen, fp);
    const shell = { ...header, globalParams: gp };
    this.globalService = this.extractPlainData(shell);
    this.USER_SETTINGS = this.extractPlainData(header.userSettingsData || {});
    this.SERVER_CONFIG = this.extractPlainData(header.serverConfigData || {});
    this.GENERAL_CONFIG = this.extractPlainData(header.generalConfigData || {});
    if (!this.GENERAL_CONFIG.base_url) {
      this.GENERAL_CONFIG.base_url =
        header.generalConfigData?.base_url || header.generalConfigData?.baseUrl || '';
    }
    this.filterParams = fp;
    this.gridData = gridPayloadFromActiveScreen(activeScreen);
  }

  /**
   * Gate function to prevent duplicate initialization
   * Ensures all required data is present before initializing
   */
  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }
    
    // Check if ALL required data is present before initializing
    if (!this.GENERAL_CONFIG || !this.filterParams || Object.keys(this.filterParams).length === 0) {
      return;
    }
    if (!this.GENERAL_CONFIG?.base_url) {
      return;
    }
    if (!this.globalService || Object.keys(this.globalService).length === 0) {
      return;
    }
    if (!this.globalService.globalParams || Object.keys(this.globalService.globalParams).length === 0) {
      return;
    }
    
    // Only initialize if we have gridData (edit mode)
    if (!this.gridData || !this.gridData.rowData) {
      return;
    }
    
    this.isInitialized = true;
    this.init();
  }

  /**
   * Initialize component data
   */
  init(): void {
    try {
      this.selectedGridRow = [];
      this.filterTaxYear = this.filterParams.tax_year || '';
      this.scenario = this.filterParams.scenario || this.getGlobalParam('scenario') || '';
      this.jcd_key = this.getGlobalParam('jcd_key') || '';

      if (this.USER_SETTINGS?.user?.sso_id) {
        this.logged_in_user = this.USER_SETTINGS.user.sso_id;
      }

      this.checkLockStatus();

      if (this.gridData && this.gridData.rowData) {
        this.selectedGridRow.push(_.clone(this.gridData.rowData));
        this.sync_id = this.selectedGridRow[0].SYNC_ID;
        this.getConfigDtls();
      }
    } catch (e) {
      /* init error */
      this.loadingData = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Check lock status for tax return
   * Action Code: 'n10cpv'
   */
  checkLockStatus(): void {
    const url = this.GENERAL_CONFIG.base_url + '/loadJsonObject';
    const params = {
      action_code: 'n10cpv',
      p_scenario: this.scenario,
      p_jcd_key: this.jcd_key,
    };
    this.jsonObjectFactory
      .getJSONObj(url, params, this.headerData).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data: any) => {
          this.is_locked = data.jsonObject;
          if (this.is_locked != undefined && this.is_locked != null &&
              this.is_locked[0]?.LOCK_STATUS == 'Y') {
            this.cancel();
            this.gtwSnackbarService.openSnackBar(
              `Tax Return has already been filed for tax year ${this.filterTaxYear}.`,
              'gtw-snackbar--red'
            );
            return;
          }
          this.cdr.detectChanges();
        },
        (error: any) => {
          /* lock status error */
        }
      );
  }

  /**
   * Retrieve existing config details for the selected sync
   * Action Code: '92aeer'
   */
  getConfigDtls(): void {
    this.loadingData = true;

    const url = this.GENERAL_CONFIG.base_url + '/loadJsonObject';
    const params = {
      action_code: '92aeer',
      sync_id: this.sync_id,
    };
    this.jsonObjectFactory
      .getJSONObj(url, params, this.headerData).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data: any) => {
          if (data.jsonObject != null && data.jsonObject.length > 0) {
            this.configObj = data.jsonObject[0];
            this.translateCron();

            if (this.configObj.START_DATE != undefined && this.configObj.START_DATE != '') {
              this.configObj.startDateObj = this.parseDateString(this.configObj.START_DATE);
            }
            if (this.configObj.END_DATE != undefined && this.configObj.END_DATE != '') {
              this.configObj.endDateObj = this.parseDateString(this.configObj.END_DATE);
            }
          }
          this.loadingData = false;
          this.cdr.detectChanges();
        },
        (error: any) => {
          this.loadingData = false;
          this.cdr.detectChanges();
        }
      );
  }

  /**
   * Parse date string in MM-DD-YYYY format to Date object
   * Handles the format returned from backend: "MM-DD-YYYY"
   */
  private parseDateString(dateStr: string): Date | null {
    if (!dateStr || dateStr === '') {
      return null;
    }
    
    // Handle MM-DD-YYYY format (from backend)
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const month = parseInt(parts[0], 10) - 1; // Month is 0-indexed
      const day = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      
      // Validate parsed values
      if (isNaN(month) || isNaN(day) || isNaN(year)) {
        return null;
      }
      
      const date = new Date(year, month, day);
      // Verify the date is valid
      if (isNaN(date.getTime())) {
        return null;
      }
      return date;
    }
    
    // Fallback to standard date parsing (for other formats)
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date;
  }

  /**
   * Translate cron expression to human-readable format
   * Uses cronstrue library if available
   */
  translateCron(): void {
    if (this.configObj.CRON_EXP) {
      try {
        // Check if cronstrue is available
        if (typeof cronstrue !== 'undefined' && cronstrue.toString) {
          const readableCron = cronstrue.toString(this.configObj.CRON_EXP);
          this.configObj.CRON_DESC = readableCron.includes('undefined') ||
            readableCron.includes('NaN') ||
            (readableCron.includes('only in') && this.isYearTokenNaN(readableCron)) ?
            'Error: Invalid tokens found!' : readableCron;
        } else {
          // Fallback: display the cron expression as-is
          this.configObj.CRON_DESC = this.configObj.CRON_EXP;
        }
      } catch (e: any) {
        this.configObj.CRON_DESC = 'Error: ' + (e?.message || 'Invalid cron expression');
      }
    } else {
      this.configObj.CRON_DESC = 'Error: Enter a valid cron expression!';
    }
  }

  /**
   * Check if year token is NaN in cron description
   */
  private isYearTokenNaN(description: string): boolean {
    // Simple check for NaN in year context
    return description.includes('NaN');
  }

  /**
   * Convert Date object to M-D-YYYY string format (without leading zeros)
   * Used for API calls - format: 1-15-2024 (month and day without leading zeros)
   */
  private getDateString(tempDate: Date | null): string {
    if (tempDate != undefined && tempDate != null && !isNaN(tempDate.getTime())) {
      const month = tempDate.getMonth() + 1; // No padding - single or double digit
      const day = tempDate.getDate(); // No padding - single or double digit
      const year = tempDate.getFullYear();
      return `${month}-${day}-${year}`;
    }
    return '';
  }

  /**
   * Validate start date and end date
   * Returns true if valid, false otherwise
   */
  private validateDates(): boolean {
    // Check if start date is empty
    if (!this.configObj.startDateObj || 
        !(this.configObj.startDateObj instanceof Date) || 
        isNaN(this.configObj.startDateObj.getTime())) {
      this.gtwSnackbarService.openSnackBar('Start date cannot be empty', 'gtw-snackbar--red');
      return false;
    }

    // Check if end date is empty
    if (!this.configObj.endDateObj || 
        !(this.configObj.endDateObj instanceof Date) || 
        isNaN(this.configObj.endDateObj.getTime())) {
      this.gtwSnackbarService.openSnackBar('End date cannot be empty', 'gtw-snackbar--red');
      return false;
    }

    // Check if start date is greater than end date
    if (this.configObj.startDateObj > this.configObj.endDateObj) {
      this.gtwSnackbarService.openSnackBar('Start date cannot be greater than end date', 'gtw-snackbar--red');
      return false;
    }

    return true;
  }

  /**
   * Save configuration data
   */
  save(): void {
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--red');
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    // Validation for cron expression
    if (this.configObj.CRON_EXP == undefined || this.configObj.CRON_EXP == null || this.configObj.CRON_EXP === '') {
      this.gtwSnackbarService.openSnackBar("Please enter all the required fields.", 'gtw-snackbar--red');
      this.isSaveClicked = false;
      this.crudLoading = false;
      this.cdr.detectChanges();
      return;
    }

    // Validation for dates
    if (!this.validateDates()) {
      this.isSaveClicked = false;
      this.crudLoading = false;
      this.cdr.detectChanges();
      return;
    }

    this.prepAndSendDetails();
  }

  /**
   * Prepare and send configuration details to service
   */
  private prepAndSendDetails(): void {
    let configObjToSave = _.clone(this.configObj);
    configObjToSave.SYNC_ID = this.sync_id;
    configObjToSave.START_DATE = this.getDateString(this.configObj.startDateObj);
    configObjToSave.END_DATE = this.getDateString(this.configObj.endDateObj);
    configObjToSave.IS_ACTIVE = this.configObj.IS_ACTIVE || 'N';
    configObjToSave.UPDATED_ON = this.getDateString(new Date());

    const lockInfo = {
      "scenario": Number(this.scenario) || this.scenario
    };
    configObjToSave.checkLockedbj = JSON.stringify([lockInfo]);

    const message = "FTC Sync Config have been updated";
    this.sendDetails(configObjToSave, message);
  }

  /**
   * Send details to service layer for persisting data
   */
  private sendDetails(configDetails: any, message: string): void {
    const pdfEngineApiUrl = this.GENERAL_CONFIG.pdf_engine_api_url || '';
    
    if (!pdfEngineApiUrl) {
      this.gtwSnackbarService.openSnackBar('PDF Engine API URL is not configured', 'gtw-snackbar--red');
      this.isSaveClicked = false;
      this.crudLoading = false;
      this.cdr.detectChanges();
      return;
    }

    this.ftcSyncConfigService.saveData(
      pdfEngineApiUrl,
      configDetails,
      this.filterParams,
      this.globalService?.globalParams || {}
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.crudLoading = false;
        
        if (result.errorMessage && result.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
          this.isSaveClicked = false;
        } else {
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          
          this.args = {
            object_id: this.sync_id,
            gridFilter: {
              object_id: this.sync_id
            }
          };
          
          this.savedOnce = true;
          
          // Emit refresh event for AngularJS grid update
          this.requestGridUpdate.emit('refresh');
          this.isSaveClicked = false;
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Cancel modal and close
   */
  cancel(): void {
    // Emit cancel event for AngularJS modal dismissal
    this.requestGridUpdate.emit('cancel');
    
    // If data was saved, emit grid update event
    if (this.savedOnce) {
      // Note: AngularJS will handle grid refresh via event listener
    }
  }

  /**
   * Get date value for HTML5 date input (yyyy-MM-dd format)
   */
  getDateValueForInput(dateValue: Date | null): string {
    if (!dateValue || !(dateValue instanceof Date) || isNaN(dateValue.getTime())) {
      return '';
    }
    
    const year = dateValue.getFullYear();
    const month = String(dateValue.getMonth() + 1).padStart(2, '0');
    const day = String(dateValue.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Handle date change from HTML5 date input (yyyy-MM-dd format)
   */
  onDateChange(dateField: 'start' | 'end', event: any): void {
    const dateValue = event.target.value;
    
    if (!dateValue) {
      if (dateField === 'start') {
        this.configObj.startDateObj = null;
      } else {
        this.configObj.endDateObj = null;
      }
      this.cdr.detectChanges();
      return;
    }
    
    // Parse yyyy-MM-dd format from HTML5 date input
    const date = new Date(dateValue + 'T00:00:00'); // Add time to avoid timezone issues
    
    // Validate parsed date
    if (isNaN(date.getTime())) {
      this.gtwSnackbarService.openSnackBar(
        `Invalid date format. Please select a valid date.`,
        'gtw-snackbar--red'
      );
      // Clear the invalid date
      if (dateField === 'start') {
        this.configObj.startDateObj = null;
      } else {
        this.configObj.endDateObj = null;
      }
      this.cdr.detectChanges();
      return;
    }
    
    // Set the date
    if (dateField === 'start') {
      this.configObj.startDateObj = date;
      // Validate start date is not greater than end date
      if (this.configObj.endDateObj && 
          this.configObj.startDateObj > this.configObj.endDateObj) {
        this.gtwSnackbarService.openSnackBar('Start date cannot be greater than end date', 'gtw-snackbar--red');
      }
    } else {
      this.configObj.endDateObj = date;
      // Validate end date is not less than start date
      if (this.configObj.startDateObj && 
          this.configObj.startDateObj > this.configObj.endDateObj) {
        this.gtwSnackbarService.openSnackBar('End date must be greater than or equal to start date', 'gtw-snackbar--red');
      }
    }
    
    this.cdr.detectChanges();
  }

  /**
   * Handle status toggle switch change
   * Converts checkbox boolean to 'Y'/'N' string values
   */
  onStatusToggle(event: any): void {
    this.configObj.IS_ACTIVE = event.target.checked ? 'Y' : 'N';
    this.cdr.detectChanges();
  }

  /**
   * Open date picker by programmatically triggering the input click
   * This allows the custom calendar icon to open the native date picker
   */
  openDatePicker(dateField: 'start' | 'end'): void {
    const inputId = dateField === 'start' ? 'config_start_date' : 'config_end_date';
    const dateInput = document.getElementById(inputId) as HTMLInputElement;
    
    if (!dateInput) {
      return;
    }
    
    // Focus the input first
    dateInput.focus();
    
    // Try to use showPicker() API if available (modern browsers)
    const inputWithPicker = dateInput as any;
    if (inputWithPicker.showPicker && typeof inputWithPicker.showPicker === 'function') {
      try {
        inputWithPicker.showPicker();
      } catch (e) {
        // Fallback to click if showPicker() fails
        dateInput.click();
      }
    } else {
      // Fallback for browsers that don't support showPicker()
      dateInput.click();
    }
  }

  private extractPlainData(value: any): any {
    if (value === null || value === undefined) {
      return value;
    }
    if (Array.isArray(value)) {
      return value.map((item) => this.extractPlainData(item));
    }
    if (typeof value === 'object') {
      const plainObject: any = {};
      Object.keys(value).forEach((key) => {
        if (key.startsWith('$$') || key.startsWith('$')) {
          return;
        }
        const currentValue = value[key];
        if (typeof currentValue === 'function') {
          return;
        }
        plainObject[key] = this.extractPlainData(currentValue);
      });
      return plainObject;
    }
    return value;
  }

  private getGlobalParam(key: string): any {
    return this.globalService?.globalParams?.[key] ?? this.globalService?.[key];
  }
}

